import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, Card } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Waypoint } from 'react-waypoint';
import { FileAttachment } from '~/modules/attachments/AttachmentsBlock';
import { CUSTOM_METADATA_BUILDER } from '~/modules/attachments/uploader/attachmentUtil';
import { CalendarLoading } from '~/modules/common/components/DetailsPage';
import StickyHeader from '~/modules/common/components/StickyHeader/StickyHeader';
import {
  useIsBreakpointDown,
  useEditableState,
  useIsDefaultEdit
} from '~/modules/common/hooks';
import { usePageTitle } from '~/modules/common/title';
import { RedirectToNotFound } from '~/modules/common/notfound';
import { RedirectToUnhandledError } from '~/modules/common/unhandled-error';
import useMeContext from '~/modules/me/useMeContext';
import UserProfileAttachmentsBanner from '~/modules/common/components/UserProfileAttachmentsBanner';
import { USER_PERMISSION } from '~/modules/common/enums';
import ResourceDetailsHeader from '../ResourceDetailsHeader';
import useResourceDetails from '../enhancers/useResourceDetails';
import { getUserPermittedActions } from './util';
import ResourceReadOnlyCard from './ResourceReadOnlyCard';
import { Certificates } from './Certificates';
import RolesInfo from './Role/RolesInfo';
import SkillsInfo from './Skills/SkillsInfo';
import ResourceCalendarCard from './ResourceCalendar/ResourceCalendarCard';
import ResourceUserDetailsContextProvider from './ResourceUserDetailsContextProvider';
import ResourceUserSnackbar from './ResourceUserSnackbar';
import ResumeCard from './ResumeCard';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column'
    },
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    minHeight: '-webkit-max-content'
  },
  others: {
    width: '100%',
    margin: theme.spacing(0),
    padding: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 0, 0, 0)
    }
  },
  userInfo: {
    flexGrow: 0,
    flexShrink: 0,
    width: '100%',
    [theme.breakpoints.up('md')]: {
      flexBasis: 200
    }
  },
  calendarLoading: { marginBottom: theme.spacing(2) }
}));

export const useBannerStyles = makeStyles(theme => ({
  bannerText: {
    padding: theme.spacing(0, 0.5, 0, 1.5)
  }
}));

const refetchFileAttachementQueries = ['attachmentsCount'];

export const ResourceUserDetails = ({
  showBackButton = true,
  canEditPreferences = true,
  userSlug
}) => {
  const classes = useStyles();
  const bannerClasses = useBannerStyles();

  const {
    featureFlags: { isPsaPraaUserProfileNudgesEnabled }
  } = useMeContext();

  const isDefaultEdit = useIsDefaultEdit();
  const isEditAllowed = isDefaultEdit || isPsaPraaUserProfileNudgesEnabled;
  const { editable, setEditable, toggleEditable } = useEditableState(
    isEditAllowed
  );

  useEffect(() => {
    setEditable(isEditAllowed);
  }, [isEditAllowed, setEditable]);

  usePageTitle([{ messageId: 'routes.userProfile' }]);

  const lgDown = useIsBreakpointDown('lg');
  const calendarAspectRatio = lgDown ? '1' : '1.5';

  const isMobile = useIsBreakpointDown('sm');
  const [smallAvatar, setSmallAvatar] = useState(isMobile);

  const onWaypointEnter = useCallback(() => {
    setSmallAvatar(isMobile);
  }, [setSmallAvatar, isMobile]);
  const onWaypointLeave = useCallback(() => {
    setSmallAvatar(true);
  }, [setSmallAvatar]);

  const { userDetails, error, isResourceDetailsLoading } = useResourceDetails({
    userSlug,
    isPsaPraaUserProfileNudgesEnabled
  });

  const notResourceUser =
    error && error.message && error.message.includes('NotResourceUser');
  const slugNotFound =
    error && error.message && error.message.includes('SlugNotFoundError1');

  if (notResourceUser || slugNotFound) {
    return <RedirectToNotFound />;
  }
  if (error) return <RedirectToUnhandledError />;

  const permittedActions = getUserPermittedActions(userDetails);

  const hasEditDocumentsPermission =
    editable && permittedActions.includes(USER_PERMISSION.EDIT_DOCUMENTS);

  const hasViewDocumentsPermission = permittedActions.includes(
    USER_PERMISSION.VIEW_DOCUMENTS
  );

  return (
    <ResourceUserDetailsContextProvider>
      <StickyHeader showMenuButton={!showBackButton && isMobile} bottomBorder>
        <ResourceDetailsHeader
          editable={editable}
          canEditAvatar={canEditPreferences}
          toggleEditable={toggleEditable}
          userDetails={userDetails}
          isResourceDetailsLoading={isResourceDetailsLoading}
          showBackButton={showBackButton}
          smallAvatar={smallAvatar}
        />
      </StickyHeader>
      <Waypoint onEnter={onWaypointEnter} onLeave={onWaypointLeave} />
      <div className={classes.root}>
        <div className={classes.userInfo}>
          <ResourceReadOnlyCard
            editable={editable}
            userDetails={userDetails}
            permittedActions={permittedActions}
            isResourceDetailsLoading={isResourceDetailsLoading}
          />
        </div>
        <Grid container spacing={2} className={classes.others}>
          {isPsaPraaUserProfileNudgesEnabled &&
            userDetails &&
            permittedActions.includes(
              'urn:replicon:user-action:edit-documents'
            ) && (
              <Grid container>
                <UserProfileAttachmentsBanner
                  userUri={userDetails.uri}
                  userSlug={userDetails.slug}
                  showProfileButton={false}
                  classes={bannerClasses}
                />
              </Grid>
            )}
          <Grid item xs={12} sm={6} lg={6}>
            {permittedActions.includes(
              'urn:replicon:user-action:view-project-role'
            ) && (
              <RolesInfo
                key="role"
                editable={
                  editable &&
                  permittedActions.includes(
                    'urn:replicon:user-action:edit-project-role'
                  )
                }
                userDetails={userDetails}
                isResourceDetailsLoading={isResourceDetailsLoading}
              />
            )}
            {permittedActions.includes(
              'urn:replicon:user-action:view-skill-assignments'
            ) && (
              <>
                <SkillsInfo
                  key="skill"
                  editable={
                    editable &&
                    permittedActions.includes(
                      'urn:replicon:user-action:edit-skill-assignments'
                    )
                  }
                  userDetails={userDetails}
                  isResourceDetailsLoading={isResourceDetailsLoading}
                />
                <Certificates
                  editable={
                    editable &&
                    permittedActions.includes(
                      'urn:replicon:user-action:edit-skill-assignments'
                    )
                  }
                  userDetails={userDetails}
                  isResourceDetailsLoading={isResourceDetailsLoading}
                />
              </>
            )}
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            {isResourceDetailsLoading ? (
              <Card className={classes.calendarLoading}>
                <CalendarLoading />
              </Card>
            ) : (
              <ResourceCalendarCard
                userDetails={userDetails}
                calendarAspectRatio={calendarAspectRatio}
              />
            )}
            {hasViewDocumentsPermission && (
              <FileAttachment
                attachToUri={userDetails ? userDetails.uri : null}
                editable={hasEditDocumentsPermission}
                isLoading={isResourceDetailsLoading}
                customMetadataFn={
                  isPsaPraaUserProfileNudgesEnabled
                    ? CUSTOM_METADATA_BUILDER.RESOURCE_NORESUME
                    : undefined
                }
                refetchQueries={
                  isPsaPraaUserProfileNudgesEnabled
                    ? refetchFileAttachementQueries
                    : undefined
                }
              />
            )}
            {isPsaPraaUserProfileNudgesEnabled &&
              userDetails &&
              hasViewDocumentsPermission && (
                <ResumeCard
                  userUri={userDetails.uri}
                  editable={hasEditDocumentsPermission}
                />
              )}
          </Grid>
        </Grid>
      </div>
      {isPsaPraaUserProfileNudgesEnabled && <ResourceUserSnackbar />}
    </ResourceUserDetailsContextProvider>
  );
};

ResourceUserDetails.propTypes = {
  userSlug: PropTypes.string,
  showBackButton: PropTypes.bool,
  canEditPreferences: PropTypes.bool
};

export default ResourceUserDetails;
